import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { fetchOrderHistory, keys } from "@/lib/queries";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Select } from "@/components/ui/Select";
import { Pagination } from "@/components/ui/Pagination";
import { SortableHeader } from "@/components/ui/SortableHeader";
import { useTableState } from "@/lib/useTableState";
import { fmtDate, fmtQty, fmtRelative, statusLabel } from "@/lib/format";
import { CurrencyAmount } from "@/components/ui/CurrencyAmount";
import { fillLeg } from "@/components/activity/amountCurrency";
import {
  ActivityScopeBar,
  ResetScopeButton,
  useActivityScope,
  useEventMarketScope,
  validateActivityScopeSearch,
  EMPTY_SCOPE,
  type ActivityScopeSearch,
} from "@/components/activity/ActivityScopeBar";
import {
  ClientIPCell,
  DeviceCell,
  MarketCell,
  ModeCurrencyCell,
  OutcomeCell,
  SideCell,
  StateRow,
  Th,
  UserCell,
} from "@/components/activity/ActivityCells";
import { useScopedPage } from "@/components/activity/useScopedPage";
import { useCurrencySearch } from "@/components/activity/useCurrencySearch";
import { feeRoleTone } from "@/components/fees/feeRole";
import { CurrencyChip } from "@/components/currency/columns";
import {
  NON_STAKE_ORDER_CURRENCY_HINT,
  StakeCurrencyFilter,
  parseStakeCurrency,
  type StakeCurrency,
} from "@/components/currency/StakeCurrencyFilter";

/**
 * The shared Event / Market / User scope, plus this page's own currency
 * filter (SM-511).
 *
 * Same control and same `?currency` key as Active Orders, and it means
 * the same thing on the terminal rows this page adds: order_history
 * keeps the currency snapshotted at placement, so a filled, canceled,
 * rejected or expired order narrows by the currency it was placed in.
 */
export const Route = createFileRoute("/_app/orders/history")({
  validateSearch: (
    raw: Record<string, unknown>,
  ): ActivityScopeSearch & { currency?: StakeCurrency } => ({
    ...validateActivityScopeSearch(raw),
    currency: parseStakeCurrency(raw.currency),
  }),
  component: OrderHistoryPage,
});

interface OrderHistoryFilters {
  status: string;
  side: string;
}

// Every status the platform can put an order in — the active set that
// still lives in order-service's `orders` table, plus the terminal set
// that only survives in `order_history`. The Order History endpoint
// unions both, so unlike Active Orders every option here can return
// rows.
const ORDER_STATUSES = [
  { value: "OPEN", label: "Open" },
  { value: "PARTIALLY_FILLED", label: "Partially filled" },
  { value: "FILLED", label: "Filled" },
  { value: "CANCELED", label: "Canceled" },
  { value: "REJECTED", label: "Rejected" },
  { value: "EXPIRED", label: "Expired" },
  { value: "FAILED", label: "Failed" },
  { value: "NEW", label: "New" },
  { value: "RESERVED", label: "Reserved" },
  { value: "PENDING_ENGINE", label: "Pending engine" },
  { value: "CANCEL_PENDING", label: "Cancel pending" },
];

function statusTone(status: string): "success" | "warning" | "danger" | "muted" {
  switch (status) {
    case "FILLED":
    case "OPEN":
      return "success";
    case "PARTIALLY_FILLED":
      return "warning";
    case "REJECTED":
    case "FAILED":
      return "danger";
    default:
      return "muted";
  }
}

function OrderHistoryPage() {
  const table = useTableState<OrderHistoryFilters>({
    defaultSort: { by: "occurred_at", dir: "desc" },
    defaultFilters: { status: "", side: "" },
  });
  const { scope, setScope, resetKey, hasScope } = useActivityScope();
  const [currency, setCurrency] = useCurrencySearch(parseStakeCurrency);
  const marketScope = useEventMarketScope(scope);
  // The currency joins the scope's reset key — same reason as every
  // other Activity page: a narrower result set must start at page 1.
  useScopedPage(`${resetKey}|${currency ?? ""}`, table.setPage);

  const params = {
    status: table.filters.status || undefined,
    side: table.filters.side || undefined,
    market_id: marketScope.marketIDs,
    user_id: scope.userId || undefined,
    currency,
  };
  const orders = useQuery({
    queryKey: keys.orderHistoryList(table.limit, table.offset, table.sort, params),
    queryFn: () =>
      fetchOrderHistory({
        limit: table.limit,
        offset: table.offset,
        sort: table.sort,
        ...params,
      }),
    enabled: !marketScope.pending,
  });

  const filtered = hasScope || table.hasActiveFilters || Boolean(currency);
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Order history</h1>
        <p className="text-sm text-muted">
          Every order ever placed, across every user and every status — filled and
          canceled orders included. Side and price are shown as the trader placed
          them.
        </p>
      </div>
      <ActivityScopeBar scope={scope} onChange={setScope}>
        <div className="w-44">
          <Select
            label="Status"
            placeholder="Any status"
            value={table.filters.status}
            onChange={(e) => table.setFilter("status", e.target.value)}
            options={ORDER_STATUSES}
          />
        </div>
        <div className="w-52">
          <StakeCurrencyFilter value={currency} onChange={setCurrency} />
        </div>
        <div className="w-32">
          <Select
            label="Side"
            placeholder="Any"
            value={table.filters.side}
            onChange={(e) => table.setFilter("side", e.target.value)}
            options={[
              { value: "BUY", label: "Buy" },
              { value: "SELL", label: "Sell" },
            ]}
          />
        </div>
        {/* basis-full: its own line under the bar, so the two-line note
            does not drag the controls beside it out of alignment. */}
        <p className="basis-full text-2xs text-t-4">{NON_STAKE_ORDER_CURRENCY_HINT}</p>
        <ResetScopeButton
          show={filtered}
          onReset={() => {
            table.reset();
            setCurrency(undefined);
            setScope(EMPTY_SCOPE);
          }}
        />
      </ActivityScopeBar>
      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle>All orders</CardTitle>
          <span className="text-2xs text-muted">{orders.data?.page.total ?? 0}</span>
        </CardHeader>
        <CardBody className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-surface-2">
                <tr>
                  <Th className="px-5">Market</Th>
                  {/* SM-469: which book and which currency. Never merged
                      into the market column — they are separate facts. */}
                  <Th>Mode · currency</Th>
                  <Th>User</Th>
                  <Th>Outcome</Th>
                  <Th>Side</Th>
                  <SortableHeader label="Price" field="price" sort={table.sort} onSortChange={table.setSort} align="right" />
                  <SortableHeader label="Qty" field="quantity" sort={table.sort} onSortChange={table.setSort} align="right" />
                  <SortableHeader label="Filled" field="filled_quantity" sort={table.sort} onSortChange={table.setSort} align="right" />
                  <Th>Fee</Th>
                  <Th>Status</Th>
                  {/* SM-821: where the order was placed from — "—" when
                      the client log recorded nothing. */}
                  <Th>IP</Th>
                  <Th>Device</Th>
                  <SortableHeader label="When" field="occurred_at" sort={table.sort} onSortChange={table.setSort} align="right" className="px-5" />
                </tr>
              </thead>
              <tbody>
                {(orders.data?.items ?? []).map((o) => (
                  <tr key={o.order_id} className="border-b border-border/60 last:border-0">
                    <MarketCell row={o} />
                    <ModeCurrencyCell row={o} />
                    <UserCell row={o} />
                    <OutcomeCell row={o} />
                    <SideCell side={o.intent_side || o.side} />
                    <td className="px-3 py-2.5 text-right font-mono">
                      {/* SM-549: the leg this order's money moved on, not
                          a hardcoded GOLD. It can differ from the chip's
                          debit leg — see amountCurrency.tsx. */}
                      <CurrencyAmount
                        currency={fillLeg(o, o.side)}
                        value={o.intent_price || o.price}
                        trimZeros
                      />
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">
                      {fmtQty(o.quantity, o.amount_precision)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">
                      {fmtQty(o.filled_quantity, o.amount_precision)}
                    </td>
                    <td className="px-3 py-2.5 text-xs">
                      {o.fee_role ? (
                        <div className="flex items-center gap-1.5">
                          {/* Three roles now (SM-327), so this cannot be a
                              binary: an unmatched value used to fall
                              through to taker styling, which would have
                              made a redemption fee look like a trading
                              one. */}
                          <Badge tone={feeRoleTone(o.fee_role)}>
                            {o.fee_role}
                          </Badge>
                          <span className="font-mono text-2xs text-t-2">
                            <CurrencyAmount
                              currency={fillLeg(o, o.side)}
                              value={o.fee_total_amount ?? "0"}
                            />
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      {/* The terminal reason ("INSUFFICIENT_LIQUIDITY",
                          "USER_CANCEL") is why a row is where it is —
                          the single most useful thing on a canceled
                          order, so it hangs off the status badge. */}
                      <Badge tone={statusTone(o.status)}>{statusLabel(o.status)}</Badge>
                      {o.reason && (
                        <span
                          className="mt-0.5 block truncate text-[10px] text-muted"
                          title={o.reason}
                        >
                          {o.reason}
                        </span>
                      )}
                    </td>
                    <ClientIPCell ip={o.client_ip} />
                    <DeviceCell ua={o.user_agent} />
                    <td
                      className="px-5 py-2.5 text-right text-2xs text-muted"
                      title={`Placed ${fmtDate(o.created_at)}`}
                    >
                      {fmtRelative(o.occurred_at || o.created_at)}
                    </td>
                  </tr>
                ))}
                {(orders.isLoading || marketScope.pending) && (
                  <StateRow colSpan={13}>Loading…</StateRow>
                )}
                {orders.isError && (
                  <StateRow colSpan={13}>Could not load order history. Retry in a moment.</StateRow>
                )}
                {orders.data && orders.data.items.length === 0 && (
                  <StateRow colSpan={13}>
                    {/* Name the currency when one is picked — an empty
                        table beside a Currency control otherwise reads
                        as a broken filter rather than an answer. */}
                    {currency ? (
                      <>
                        No orders in <CurrencyChip currency={currency} />
                        {hasScope || table.hasActiveFilters ? " for these filters" : ""}.
                      </>
                    ) : (
                      <>No orders{filtered ? " for these filters" : ""}.</>
                    )}
                  </StateRow>
                )}
              </tbody>
            </table>
          </div>
          {orders.data && (
            <Pagination
              page={orders.data.page}
              currentPage={table.page}
              onPageChange={table.setPage}
              itemLabel="orders"
            />
          )}
        </CardBody>
      </Card>
    </div>
  );
}
